import React, { useState, useEffect, useCallback } from "react";
import { Table, Button, Checkbox, Tag, Dropdown, Modal, message } from "antd";
import { PlusOutlined, MoreOutlined } from "@ant-design/icons";
import {
  getAttributeMappings,
  getEnabledAttributeMappings,
  deleteAttributeMapping,
  updateAttributeMapping,
} from "../../../services/integrations/klaviyoAttributeMappingsService";
import AttributeMappingModal from "./AttributeMappingModal";

export default function AttributeMappingsTab({ subscriptionId }) {
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEnabledOnly, setShowEnabledOnly] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editMapping, setEditMapping] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    try {
      const res = showEnabledOnly
        ? await getEnabledAttributeMappings()
        : await getAttributeMappings();
      setMappings(Array.isArray(res) ? res : []);
    } catch (err) {
      if (err?.response?.status !== 404) {
        message.error("Failed to load attribute mappings");
      }
      setMappings([]);
    }
    setLoading(false);
  }, [showEnabledOnly]);

  useEffect(() => {
    if (subscriptionId) {
      fetchMappings();
    }
  }, [subscriptionId, fetchMappings]);

  const handleEdit = (mapping) => {
    setEditMapping(mapping);
    setModalVisible(true);
  };

  const handleAdd = () => {
    setEditMapping(null);
    setModalVisible(true);
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteAttributeMapping(deleteTarget.id);
      message.success("Attribute mapping deleted");
      setDeleteTarget(null);
      fetchMappings();
    } catch (err) {
      message.error("Failed to delete attribute mapping");
    }
  };

  const handleToggleEnabled = async (mapping) => {
    try {
      await updateAttributeMapping(mapping.id, { enabled: !mapping.enabled });
      message.success(mapping.enabled ? "Mapping disabled" : "Mapping enabled");
      fetchMappings();
    } catch (err) {
      message.error("Failed to update mapping");
    }
  };

  const columns = [
    {
      title: "Polly Attribute",
      dataIndex: "attributeName",
      key: "attributeName",
      render: (text, record) => text || `Attribute #${record.dataClassAttributeId}`,
    },
    {
      title: "Direction",
      dataIndex: "direction",
      key: "direction",
      width: 110,
      render: (dir) =>
        dir === "OUTBOUND" ? (
          <span title="Outbound">{"\u2192"} Outbound</span>
        ) : (
          <span title="Inbound">{"\u2190"} Inbound</span>
        ),
    },
    {
      title: "Vendor Field",
      key: "vendorField",
      render: (_, record) =>
        record.providerFieldOutbound || record.providerFieldInbound || "-",
    },
    {
      title: "Type Intent",
      dataIndex: "typeIntent",
      key: "typeIntent",
      width: 110,
      render: (text) => text || "-",
    },
    {
      title: "Status",
      key: "status",
      width: 100,
      render: (_, record) =>
        record.enabled ? (
          <Tag color="green">Active</Tag>
        ) : (
          <Tag color="default">Disabled</Tag>
        ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 120,
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <a onClick={() => handleEdit(record)}>Edit</a>
          <Dropdown
            menu={{
              items: [
                {
                  key: "toggle",
                  label: record.enabled ? "Disable" : "Enable",
                  onClick: () => handleToggleEnabled(record),
                },
                {
                  key: "delete",
                  label: "Delete",
                  danger: true,
                  onClick: () => setDeleteTarget(record),
                },
              ],
            }}
            trigger={["click"]}
          >
            <MoreOutlined style={{ cursor: "pointer", fontSize: 16 }} />
          </Dropdown>
        </div>
      ),
    },
  ];

  if (loading) return null;

  if (mappings.length === 0 && !showEnabledOnly) {
    return (
      <div className="event-mappings-empty">
        <h3>No attribute mappings configured.</h3>
        <p>
          Map Polly attributes to Klaviyo profile properties to sync data between systems.
        </p>
        <div className="empty-actions">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="standard-button primary-orange-button"
            onClick={handleAdd}
          >
            Add Attribute Mapping
          </Button>
        </div>

        <AttributeMappingModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          onSaved={fetchMappings}
          editMapping={editMapping}
        />
      </div>
    );
  }

  return (
    <>
      <div className="event-mappings-toolbar">
        <div className="toolbar-left">
          <Button
            type="primary"
            icon={<PlusOutlined />}
            className="standard-button primary-orange-button"
            onClick={handleAdd}
          >
            Add Attribute Mapping
          </Button>
          <Checkbox
            checked={showEnabledOnly}
            onChange={(e) => setShowEnabledOnly(e.target.checked)}
          >
            Show enabled only
          </Checkbox>
        </div>
      </div>
      <Table
        dataSource={mappings}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={false}
        size="middle"
      />

      <AttributeMappingModal
        visible={modalVisible}
        onClose={() => setModalVisible(false)}
        onSaved={fetchMappings}
        editMapping={editMapping}
      />

      <Modal
        title="Delete Attribute Mapping"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
      >
        <p>
          Are you sure you want to delete the mapping for{" "}
          <strong>{deleteTarget?.attributeName || `Attribute #${deleteTarget?.dataClassAttributeId}`}</strong>?
          This cannot be undone.
        </p>
      </Modal>
    </>
  );
}
